// A scanned section sticker: what sits on it, and a field to put more boxes there. Every
// scan moves one whole box to this section, says so under the field, then re-reads the
// section so the list below is the truth. The field stays ready for the next box.

import React, { useCallback, useState } from 'react';
import { usePullsApi } from '../../Parts/Pulls/pullsApi';
import { useStickerLookup } from '../../utils/useStickerLookup';
import BoxScan from '../BoxScan';
import { serverMessage } from '../errors';
import { buzz } from '../shell';
import { Notice, ScreenBar } from '../ui';
import { itemName } from '../Box/sentences';
import type { SectionBox, SectionPayload } from './types';

interface Props {
  section: SectionPayload;
  onBack: () => void;
}

const BoxRow: React.FC<{ box: SectionBox }> = ({ box }) => (
  <li className="rounded-xl bg-surface-container px-4 py-3">
    <span className="block font-mono text-sm text-on-surface-variant">{box.barcode}</span>
    <ul className="mt-1 flex flex-col gap-0.5">
      {box.items.map((i) => (
        <li key={i.receipt_item_id} className="flex items-baseline justify-between gap-3 text-base text-on-surface">
          <span className="min-w-0 truncate font-medium">{itemName(i)}</span>
          <span className="shrink-0 font-mono tabular-nums text-on-surface-variant">{i.quantity_remaining}</span>
        </li>
      ))}
    </ul>
  </li>
);

const SectionCard: React.FC<Props> = ({ section: initial, onBack }) => {
  const pulls = usePullsApi();
  const { lookup } = useStickerLookup();
  const [section, setSection] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [n, setN] = useState(0);

  const putHere = useCallback(async (code: string) => {
    if (/^LOC-/i.test(code)) {
      setDone(null);
      buzz('reject');
      setError('That is a section label. Scan a box.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await pulls.moveBox(code, { to_location_id: section.id });
      const said = `${r?.items?.[0]?.barcode || code.toUpperCase()} is on ${r?.to?.code || section.code}.`;
      setDone(said);
      setN((x) => x + 1);
      const fresh = await lookup(`LOC-${section.code}`);
      if (fresh.status === 'found' && fresh.result.payload?.code) setSection(fresh.result.payload as SectionPayload);
      else setDone(`${said} The list may be out of date.`);
    } catch (err) {
      setDone(null);
      buzz('reject');
      setError(serverMessage(err, 'That did not go through. Scan the box again.'));
    } finally {
      setBusy(false);
    }
  }, [section.id, section.code]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScreenBar
        title={(
          <span className="flex items-center gap-2">
            <span className="font-mono tabular-nums">{section.code}</span>
            <span className="truncate font-normal text-on-surface-variant">{section.area.name}</span>
          </span>
        )}
        onBack={onBack}
      />
      <div className="flex min-h-0 w-full max-w-md flex-1 flex-col gap-3 self-center overflow-y-auto px-4 pb-6">
        <BoxScan id="scanner-section-scan" label="Put boxes here" placeholder="Scan a box" busy={busy} onScan={putHere} />
        <div aria-live="polite">
          {done && <div key={n}><Notice tone="ok">{done}</Notice></div>}
          {error && <div role="alert"><Notice tone="miss">{error}</Notice></div>}
        </div>

        <section aria-label="On this section" className="flex flex-col gap-2">
          <h2 className="px-1 text-base font-semibold text-on-surface">On this section</h2>
          {section.boxes.length === 0 ? (
            <p className="rounded-xl bg-surface-container-low px-4 py-3 text-base text-on-surface-variant">
              Nothing here yet. Scan a box to put it on {section.code}.
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {section.boxes.map((b) => <BoxRow key={b.barcode} box={b} />)}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default SectionCard;
